import React from "react";
import {
  formatSearchLocationText,
  normalizeSearchDataList,
  stringifyForDisplay,
} from "../utils";

const JsonDisclosure = ({ title, raw }) => {
  const body = stringifyForDisplay(raw);
  if (!body) return null;

  return (
    <details className="logs-searchdata-json">
      <summary>{title}</summary>
      <pre className="logs-searchdata-pre">{body}</pre>
    </details>
  );
};

const requestTypeVariant = (requestType) => {
  const rt = String(requestType || "").toLowerCase();
  if (rt === "rate") return "rate";
  if (rt === "order") return "order";
  return "default";
};

const SearchDataCard = ({ item, index, total }) => {
  const requestType = item.requestType || "—";
  const typeVariant = requestTypeVariant(item.requestType);

  return (
    <article className="logs-searchdata-card">
      <header className="logs-searchdata-card__head">
        <div>
          <span className="logs-searchdata-card__index">
            Record {index + 1}
            {total > 1 ? ` of ${total}` : null}
          </span>
          <h4 className="logs-searchdata-card__title">CTrip request data</h4>
        </div>
        <span
          className={`logs-searchdata-type-pill logs-searchdata-type-pill--${typeVariant}`}
        >
          {requestType}
        </span>
      </header>

      <div className="logs-searchdata-meta">
        <div className="logs-searchdata-field">
          <span>Record ID</span>
          <strong>{item.id || "—"}</strong>
        </div>
        <div className="logs-searchdata-field">
          <span>Search session ID</span>
          <strong>{item.uniqueId || "—"}</strong>
        </div>
        <div className="logs-searchdata-field">
          <span>Airport ID</span>
          <strong>{item.airportId || "—"}</strong>
        </div>
        <div className="logs-searchdata-field">
          <span>Service level</span>
          <strong>{item.serviceLevel || "—"}</strong>
        </div>
        <div className="logs-searchdata-field">
          <span>Rate</span>
          <strong>{item.rate != null ? String(item.rate) : "—"}</strong>
        </div>
        <div className="logs-searchdata-field">
          <span>Distance</span>
          <strong>{item.distance != null ? String(item.distance) : "—"}</strong>
        </div>
        <div className="logs-searchdata-field">
          <span>Passenger</span>
          <strong>{item.passenger || "—"}</strong>
        </div>
        <div className="logs-searchdata-field logs-searchdata-field--wide">
          <span>Notes</span>
          <strong>{item.notes || "—"}</strong>
        </div>
      </div>

      <div className="logs-searchdata-route">
        <div className="logs-searchdata-route__col">
          <span className="logs-searchdata-route__label">Origin</span>
          <p className="logs-searchdata-route__text">
            {formatSearchLocationText(item.origin)}
          </p>
          {(item.originLatitude != null || item.originLongitude != null) && (
            <small className="logs-searchdata-route__coords">
              {item.originLatitude ?? "—"}, {item.originLongitude ?? "—"}
            </small>
          )}
        </div>
        <div className="logs-searchdata-route__arrow" aria-hidden>
          →
        </div>
        <div className="logs-searchdata-route__col">
          <span className="logs-searchdata-route__label">Destination</span>
          <p className="logs-searchdata-route__text">
            {formatSearchLocationText(item.destination)}
          </p>
          {(item.destinationLatitude != null ||
            item.destinationLongitude != null) && (
            <small className="logs-searchdata-route__coords">
              {item.destinationLatitude ?? "—"},{" "}
              {item.destinationLongitude ?? "—"}
            </small>
          )}
        </div>
      </div>

      <div className="logs-searchdata-payloads">
        <JsonDisclosure title="Request payload" raw={item.request} />
        <JsonDisclosure title="Response payload" raw={item.response} />
        {item.algoData != null && item.algoData !== "" ? (
          <JsonDisclosure title="Algorithm / pricing data (algoData)" raw={item.algoData} />
        ) : null}
      </div>
    </article>
  );
};

const SearchDataSection = ({ searchId, items }) => {
  const list = normalizeSearchDataList(items);
  if (!list.length) return null;

  return (
    <section className="logs-searchdata-section" aria-label="Search and rate request data">
      <div className="logs-searchdata-section__head">
        <div>
          <h3>Search &amp; rate data</h3>
          <p className="logs-searchdata-section__sub">
            Trip.com / CTrip request records linked to this booking&apos;s search
            session
            {searchId ? (
              <>
                {" "}
                (<code className="logs-searchdata-code">{searchId}</code>)
              </>
            ) : null}
          </p>
        </div>
        <span className="logs-searchdata-count">{list.length} record(s)</span>
      </div>

      <div className="logs-searchdata-stack">
        {list.map((item, index) => (
          <SearchDataCard
            key={item.id || `search-data-${index}`}
            item={item}
            index={index}
            total={list.length}
          />
        ))}
      </div>
    </section>
  );
};

export default SearchDataSection;
